import {useState, useEffect, useCallback} from 'react';
import axiosInstance from '../../../Config/axiosInstance';
import {Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Typography, CircularProgress, Alert, Box, Menu, MenuItem, ListItemIcon, ListItemText} from '@mui/material';
import {Save, PictureAsPdf, GetApp, Assessment} from '@mui/icons-material';
import * as ExcelJS from 'exceljs';
import './UnfinishedProductionPM.css';
import logoImg from '../../../Images/logo_pmpc.png';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const UnfinishedProductionPM = () => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [error, setError] = useState(null);
    const [reportGenerated, setReportGenerated] = useState(false);

    const fetchReportData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.get('/unfinished-production-pm/');

            if (!response.data || response.data.length === 0) {
                setError('No data found! No projects with unfinished production greater than 500!');
                setReportData([]);
                return;
            }

            setReportData(response.data);
            setReportGenerated(true);
        } catch (err) {
            console.error('Error generating report:', err);
            setError(`Error: ${err.response?.data?.message || err.message}`);
            setReportData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const formatNumber = (num) => {
        if(num == null || num === undefined || num === '' || isNaN(Number(num))) {
            return '0.00';
        }
        const number = typeof num === 'string' ? parseFloat(num) : num;
        return new Intl.NumberFormat('en-EN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        }).format(number);
    };

    const formatPercentage = (num) => {
        if(num == null || num === undefined || num === '' || isNaN(Number(num))) {
            return '0.00%';
        }
        const number = typeof num === 'string' ? parseFloat(num) : num;
        return new Intl.NumberFormat('en-EN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        }).format(number) + '%';
    };

    const exportToExcel = async () => {
        if(!reportData) {
            alert('No data to report');
            return;
        }

        const title = 'Unfinished Production Report';
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Report');

        // Logo
        try {
            sheet.mergeCells('A1:A4');
            const logo = workbook.addImage({
                buffer: await fetch(logoImg).then(res => res.arrayBuffer()),
                extension: 'png',
            });

            sheet.addImage(logo, {
                tl: {col: 0, row: 0},
                br: {col: 1, row: 4}
            })
        } catch (error) {
            console.warn('Logo could not be loaded:', error);
        }

        //titlu
        sheet.mergeCells('B1:F4');
        const titleCell = sheet.getCell('B1');
        titleCell.value = title;
        titleCell.font = {size: 14, bold: true};
        titleCell.alignment = {
            vertical: 'middle', 
            horizontal: 'center', 
            wrapText: true
        };

        //rand liber
        sheet.insertRow(5, []);

        //header
        const headerRowNumber = 6;
        const headers = [
            'Project Code', 
            'Project Description', 
            'Percentage',
            'Worked Value',
            'Total Invoiced',
            'Unfinished Production'
        ];

        const headerRow = sheet.getRow(headerRowNumber);
        headerRow.values = headers;
        headerRow.height = 30;
        headerRow.font = {bold: true};

        headerRow.eachCell((cell, colNumber) => {
            const alignRightColumns = [3, 4, 5, 6]; 
            cell.alignment = {
                vertical: 'middle', 
                horizontal: alignRightColumns.includes(colNumber) ? 'right' : 'left',
                wrapText: colNumber === 2
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid', 
                fgColor: {argb: 'FFE8E8E8'}
            };
        });

        //date
        let currentRow = headerRowNumber + 1;
        reportData.forEach(item => {
            const row = sheet.getRow(currentRow);
            
            const procentValue = Number(item.Procent || item.procent || 0);
            row.values = [
                item.ProjectCode || item.project_code,
                item.DescriereProiect || item.project_description || '',
                procentValue / 100, //impartim la 100 pentru formatul Excel
                Number(item.ValoareMuncita || item.valoare_muncita || 0),
                Number(item.TotalFacturat || item.total_facturat || 0),
                Number(item.ProductieNeterminata || item.productie_neterminata || 0)
            ];

            row.eachCell((cell, colNumber) => {
                const isRightAligned = colNumber >= 3 && colNumber <= 6;
                cell.alignment = {
                    vertical: colNumber === 2 ? 'top' : 'middle',
                    horizontal: isRightAligned ? 'right' : 'left',
                    wrapText: colNumber === 2
                };
                
                if(colNumber === 3) {
                    cell.numFmt = '0.00%';
                } else if(colNumber >= 4 && colNumber <= 6) {
                    cell.numFmt = '#,##0.00';
                }
            });
            currentRow++;
        });

        //dimensiuni coloane, prima coloana fixa 
        sheet.getColumn(1).width = 35;  

        //dimensiuni automate pentru restul coloanelor
        for (let i = 2; i <= 6; i++) {
            let maxLength = 0;
            const column = sheet.getColumn(i);

            const header = headers[i - 1];
            if (header && header.length > maxLength) {
                maxLength = header.length;
            }
            
            column.eachCell({ includeEmpty: false }, (cell) => {
                if (cell.value !== null && cell.value !== undefined) {
                    const cellLength = cell.value.toString().length;
                    if (cellLength > maxLength) {
                        maxLength = cellLength;
                    }
                }
            });

            const calculatedWidth = maxLength + 2; 
            column.width = Math.min(Math.max(calculatedWidth, 10), 50);
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const today = new Date().toISOString().split('T')[0];
        const fileName = `UnfinishedProductionReport_${today}.xlsx`;
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    };

    const exportPDF = async () => {
        if(!reportData) {
            alert('No data to export!');
            return;
        }

        //pdf portret
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const logo = "/logo_pmpc.png";

        //logo
        pdf.addImage(logo, "PNG", 8, 3, 28, 8);

        //dimensiuni elemente decorative
        const triangleWidth = 20;
        const totalWidth = pdfWidth * 0.25;
        const barWidth = totalWidth - triangleWidth;

        const barHeight = 1.2; //bara scurta + triunghi
        const secondBarHeight = 2; //bara lunga
        const barTop = 15;
        const barBottom = barTop + barHeight;

        //bara scurta
        pdf.setFillColor(23, 52, 88);
        const barOverlap = 1;
        pdf.rect(0, barTop, barWidth + barOverlap, barHeight, "F");

        //triunghi
        pdf.triangle(
            barWidth, barBottom,
            barWidth + triangleWidth, barBottom,
            barWidth, barTop,
            "F" 
        );

        //suprapunere
        const overlap = 0.6;
        const headerLongBarTop = barBottom - overlap;

        //bara lunga full-width (header)
        pdf.rect(0, headerLongBarTop, pdfWidth, secondBarHeight, "F");
        const dottedLineY = headerLongBarTop + secondBarHeight;

        // Titlu
        const reportTitle = "Unfinished Production Report";
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(reportTitle, pdfWidth / 2, dottedLineY + 12, {align: "center"});

        const contentTop = dottedLineY + 20;
        let currentY = contentTop;

        // Date pentru tabel
        const tableData = reportData.map(item => [
            item.ProjectCode || item.project_code,
            item.DescriereProiect || item.project_description || '',
            formatPercentage(item.Procent || item.procent || 0),
            formatNumber(item.ValoareMuncita || item.valoare_muncita || 0),
            formatNumber(item.TotalFacturat || item.total_facturat || 0),
            formatNumber(item.ProductieNeterminata || item.productie_neterminata || 0)
        ]);

        const tableHeaders = [
            "Project Code", 
            "Project Description", 
            "Percentage",
            "Worked Value",
            "Total Invoiced",
            "Unfinished Production"
        ];

        const columnStyles = {
            0: {cellWidth: 50}, 
            1: {cellWidth: 50}, 
            2: {cellWidth: 25, halign: 'right'},
            3: {cellWidth: 20, halign: 'right'},
            4: {cellWidth: 20, halign: 'right'},
            5: {cellWidth: 25, halign: 'right'}
        };

        //adauga tabelul
        autoTable(pdf, {
            startY: currentY,
            head: [tableHeaders],
            body: tableData,
            styles: {
                fontSize: 9, 
                cellPadding: 2,
                overflow: "linebreak",
                lineColor: [180, 180, 180],
                lineWidth: 0.25,
                valign: "middle",
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [232, 232, 232],
                textColor: 0, 
                fontStyle: "bold",
                halign: "center",
            },
            columnStyles: columnStyles, 
            alternateRowStyles: {
                fillColor: [245, 247, 250]
            },
            margin: {left: 10, right: 10}, 
            tableWidth: 'auto',
            theme: 'grid',
            didDrawPage: function(data) {
                if (data.cursor) {
                    currentY = data.cursor.y;
                }
            }
        });

        //footer pentru toate paginile
        const totalPages = pdf.internal.getNumberOfPages();
        
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            
            pdf.setFillColor(23, 52, 88);

            const bottomOffset = 12; 
            const longBarTop = pdfHeight - bottomOffset; 

            //suprapunere
            const verticalOverlapFooter = 0.6;

            //bara lunga jos
            pdf.rect(
            0,
            longBarTop - verticalOverlapFooter + 0.1,
            pdfWidth,
            secondBarHeight,
            "F"
            );

            //bara scurta + triunghi
            const shortBarTop = longBarTop + secondBarHeight - verticalOverlapFooter;
            const shortBarBottom = shortBarTop + barHeight;

            //bara scurta 
            const barOverlapFooter = 1;
            pdf.rect(
            0,
            shortBarTop,
            barWidth + barOverlapFooter,
            barHeight,
            "F"
            );

            //triunghiul din partea dreapta jos
            pdf.triangle(
            barWidth, shortBarTop,                    
            barWidth + triangleWidth, shortBarTop,        
            barWidth, shortBarBottom,                      
            "F"
            );

            //pagina curenta/ total pagini
            pdf.setFontSize(7);
            pdf.setFont("helvetica", "normal");
            pdf.text("Generated by PMPC ERP", 10, pdfHeight - 3);
            const today = new Date();
            const currentDate = today.toLocaleDateString("ro-RO");
            pdf.text(`${currentDate} `, pdfWidth - 30, pdfHeight - 3);

            pdf.text(`Page ${i} of ${totalPages}`, pdfWidth / 2, pdfHeight - 3, { align: "center" });
        }

        //save pdf
        const today = new Date().toISOString().split('T')[0]; // "2026-01-02"
        const fileName = `UnfinishedProduction_${today}.pdf`;
        pdf.save(fileName);
    };

    return (
        <Container maxWidth={false} disableGutters className='rep-unfinished-prod unfinished-prod'>
            <Container maxWidth={false} disableGutters className='unfinished-prod-container'>
                <Typography variant='h4' gutterBottom marginTop='20px'>
                    Unfinished Production Report
                </Typography>

                <Paper className='filters-paper' sx={{p: 2}}>
                    <Box display='flex' alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                        <Box display='flex' alignItems='center' gap={2}>
                            <Button
                                variant='contained'
                                color='primary'
                                className='generate-button'
                                onClick={fetchReportData}
                                startIcon={loading ? <CircularProgress size={20}/> : <Assessment/>}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Refresh Report'}
                            </Button>
                        </Box>
                        <Box>
                            <Button
                                variant='contained'
                                size='small'
                                onClick={handleClick}
                                className='save-button'
                                startIcon={<Save/>}
                                disabled={!reportGenerated || !reportData}
                            >
                                Save As
                            </Button>
                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                                <MenuItem onClick={() => {exportPDF(); handleClose();}}>
                                    <ListItemIcon><PictureAsPdf fontSize='small'/></ListItemIcon> 
                                    <ListItemText>Save as PDF</ListItemText>
                                </MenuItem>
                                <MenuItem onClick={() => {exportToExcel(); handleClose();}}>
                                    <ListItemIcon><GetApp fontSize='small'/></ListItemIcon>
                                    <ListItemText>Save as Excel</ListItemText>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{mt: 2}}>
                        {error}
                    </Alert>
                )}

                {loading && !reportData ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', mt: 4}}>
                        <CircularProgress/>
                    </Box>
                ) : reportGenerated && reportData ? (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, mt: 3}}>
                        <Paper className='filters-paper' sx={{p: 2}}>
                            <TableContainer className='report-table-containter'>
                                <Table stickyHeader size='small' sx={{minWidth: 800}}>
                                    <TableHead>
                                        <TableRow className='table-header'>
                                            <TableCell className='table-header-cell'>Project Code</TableCell>
                                            <TableCell className='table-header-cell'>Project Description</TableCell>
                                            <TableCell className='table-header-cell' align='right'>Percentage</TableCell>
                                            <TableCell className='table-header-cell' align='right'>Worked Value</TableCell>
                                            <TableCell className='table-header-cell' align='right'>Total Invoiced</TableCell>
                                            <TableCell className='table-header-cell' align='right'>Unfinished Production</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{row.ProjectCode || row.project_code}</TableCell>
                                                <TableCell>{row.DescriereProiect || row.project_description || ''}</TableCell>
                                                <TableCell align='right'>{formatPercentage(row.Procent || row.procent || 0)}</TableCell>
                                                <TableCell align='right'>{formatNumber(row.ValoareMuncita || row.valoare_muncita || 0)}</TableCell>
                                                <TableCell align='right'>{formatNumber(row.TotalFacturat || row.total_facturat || 0)}</TableCell>
                                                <TableCell align='right'>{formatNumber(row.ProductieNeterminata || row.productie_neterminata || 0)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        <Box className="report-footer" sx={{p: 1, bgcolor: 'grey.100', borderRadius: 1}}>
                            <Typography variant='caption' className='footer-text'>
                                Generated on {new Date().toLocaleDateString('ro-RO')} • 
                                Showing {reportData.length} projects with unfinished production &gt; 500
                            </Typography>
                        </Box>
                    </Box>
                ) : reportGenerated && !reportData ? (
                    <Alert severity="warning" sx={{mt: 2}}>
                        Report generated but no data available to display.
                    </Alert>
                ) : null}
            </Container>
        </Container>
    );
};

export default UnfinishedProductionPM;